import { useState, type FormEvent } from 'react';
import { cn } from '@/lib/utils';
import { useWaitlist } from '@/hooks/useWaitlist';
import { Input } from '@/components/ui/Input';

type Tab = 'shopper' | 'merchant';

export function WaitlistCta() {
  const [tab, setTab] = useState<Tab>('shopper');
  const [email, setEmail] = useState('');
  const [storeName, setStoreName] = useState('');
  const { submitted, submit } = useWaitlist();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;

    submit({
      type: tab,
      email,
      storeName: tab === 'merchant' ? storeName : undefined,
      submittedAt: new Date().toISOString(),
    });
  }

  return (
    <section
      id="waitlist"
      className="bg-gradient-to-br from-brand-500 to-brand-600 py-24"
    >
      <div className="container mx-auto max-w-lg px-6 text-center">
        {submitted ? (
          /* Success state */
          <div className="flex flex-col items-center gap-4">
            <svg
              width={64}
              height={64}
              viewBox="0 0 64 64"
              fill="none"
              className="text-white"
            >
              <circle
                cx="32"
                cy="32"
                r="30"
                stroke="currentColor"
                strokeWidth="3"
                className="animate-scale-in"
              />
              <path
                d="M20 33l8 8 16-16"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="100"
                className="animate-draw-check"
              />
            </svg>
            <h2 className="text-3xl font-bold text-white">
              You're on the list!
            </h2>
            <p className="text-white/80">
              We'll let you know when Run2Sale launches in your area.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-3xl font-bold text-white md:text-4xl">
              Be First In Line
            </h2>
            <p className="mt-3 text-white/80">
              Join the waitlist and get early access when we launch.
            </p>

            {/* Tab toggle */}
            <div className="mt-8 inline-flex rounded-full bg-white/20 p-1">
              {(['shopper', 'merchant'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={cn(
                    'rounded-full px-6 py-2 text-sm font-medium capitalize transition-colors',
                    tab === t
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-white hover:bg-white/10',
                  )}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-white/20 bg-white/10 text-white placeholder:text-white/50 focus:border-white focus:ring-white/30"
              />
              {tab === 'merchant' && (
                <Input
                  placeholder="Store name"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  className="border-white/20 bg-white/10 text-white placeholder:text-white/50 focus:border-white focus:ring-white/30"
                />
              )}
              <button
                type="submit"
                className="w-full rounded-lg bg-white px-6 py-3 text-base font-semibold text-brand-600 shadow-md transition-all hover:bg-white/90 hover:shadow-lg"
              >
                {tab === 'shopper'
                  ? 'Join Shopper Waitlist'
                  : 'Register Merchant Interest'}
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
}
